import React from 'react';
import { FD130, FD131 } from '../../../services/d203Api';
import { fmt, COLOR_CATEGORY } from '../helpers';

interface Tab4Props {
  formData: Partial<FD130>;
  details: Partial<FD131>[];
  selectedRow: number;
  isEditing: boolean;
  setSelectedRow: (idx: number) => void;
  getDetailField: (key: keyof FD131) => FD131[keyof FD131];
  setDetailField: (key: keyof FD131, value: FD131[keyof FD131]) => void;
  addDetailRow: () => void;
  deleteDetailRow: () => void;
  getField: (key: keyof FD130) => FD130[keyof FD130];
  names: Record<string, string>;
  resolveSingleName: (nameKey: string, type: string, code: string) => void;
}

const Tab4Detail: React.FC<Tab4Props> = React.memo(({
  formData, details, selectedRow, isEditing,
  setSelectedRow, getDetailField, setDetailField,
  addDetailRow, deleteDetailRow,
  getField, names, resolveSingleName,
}) => {
  const disabled = !isEditing;
  const pricingMethod = formData.fd130_40 || 1;
  const sel = details[selectedRow] as Partial<FD131> | undefined;

  return (
    <>
      {/* Total Items */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 13 }}>
        <label>色號明細總項數</label>
        <input type="text" readOnly style={{ maxWidth: 60 }}
          value={getField('fd130_42') as number || 0} data-contract-field="fd130_42"
        />
      </div>

      {/* Grid */}
      <div className="table-wrapper">
        <div className="table-toolbar">
          <div className="left">
            <span style={{ fontSize: 13 }}>色號明細 ({details.length} 項)</span>
          </div>
          <div className="right">
            <button className="btn btn-sm" disabled={disabled} onClick={addDetailRow}>明細新增</button>
            <button className="btn btn-sm btn-danger" disabled={disabled || details.length === 0} onClick={deleteDetailRow}>明細刪除</button>
          </div>
        </div>
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 50 }} data-contract-field="fd131_02">項次</th>
                <th data-contract-field="fd131_11">色號</th>
                <th className="num" data-contract-field="fd131_13">成品量</th>
                <th className="num" data-contract-field="fd131_14">指染量</th>
                <th className="num" data-contract-field="fd131_16">工繳單價</th>
                <th className="num" data-contract-field="fd131_26">一缸工繳</th>
                <th data-contract-field="fd131_18">S/C NO</th>
                <th data-contract-field="fd131_17">S/C項次</th>
                <th className="num" data-contract-field="fd131_242">允收增加量+</th>
                <th className="num" data-contract-field="fd131_241">允收比率+</th>
                <th className="num" data-contract-field="fd131_244">允收增加量-</th>
                <th className="num" data-contract-field="fd131_243">允收比率-</th>
              </tr>
            </thead>
            <tbody>
              {details.length === 0 ? (
                <tr><td colSpan={12} style={{ textAlign: 'center', color: 'var(--text-tertiary)' }}>無明細</td></tr>
              ) : details.map((d, i) => (
                <tr key={i}
                  className={selectedRow === i ? 'selected' : ''}
                  onClick={() => setSelectedRow(i)}
                >
                  <td>{d.fd131_02}</td>
                  <td>{d.fd131_11 || ''}</td>
                  <td className="num">{fmt(d.fd131_13)}</td>
                  <td className="num">{fmt(d.fd131_14)}</td>
                  <td className="num">{fmt(d.fd131_16)}</td>
                  <td className="num">{fmt(d.fd131_26)}</td>
                  <td>{d.fd131_18 || ''}</td>
                  <td>{d.fd131_17 || ''}</td>
                  <td className="num">{fmt(d.fd131_242)}</td>
                  <td className="num">{fmt(d.fd131_241)}</td>
                  <td className="num">{fmt(d.fd131_244)}</td>
                  <td className="num">{fmt(d.fd131_243)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Panel (below grid) — edit fields of selected row */}
      {sel && (
        <div className="detail-panel">
          <label>色號</label>
          <input type="text" style={{ maxWidth: 120 }}
            disabled={disabled}
            value={getDetailField('fd131_11') as string}
            onChange={e => setDetailField('fd131_11', e.target.value)} data-contract-field="fd131_11"
          />

          <label>色號名稱</label>
          <input type="text" style={{ maxWidth: 160 }}
            disabled={disabled}
            value={getDetailField('fd131_12') as string}
            onChange={e => setDetailField('fd131_12', e.target.value)} data-contract-field="fd131_12"
          />

          <label>成品量</label>
          <input type="number" style={{ maxWidth: 100 }} step="0.1"
            disabled={disabled}
            value={getDetailField('fd131_13') as number || ''}
            onChange={e => setDetailField('fd131_13', parseFloat(e.target.value) || 0)} data-contract-field="fd131_13"
          />

          <label>指染量</label>
          <input type="number" style={{ maxWidth: 100 }} step="0.1"
            disabled={disabled}
            value={getDetailField('fd131_14') as number || ''}
            onChange={e => setDetailField('fd131_14', parseFloat(e.target.value) || 0)} data-contract-field="fd131_14"
          />

          <label>工繳單價</label>
          <input type="number" style={{ maxWidth: 100 }} step="0.01"
            disabled={disabled || pricingMethod !== 1}
            value={getDetailField('fd131_16') as number || ''}
            onChange={e => setDetailField('fd131_16', parseFloat(e.target.value) || 0)} data-contract-field="fd131_16"
          />

          <label>一缸工繳</label>
          <input type="number" style={{ maxWidth: 100 }} step="0.01"
            disabled={disabled}
            value={getDetailField('fd131_26') as number || ''}
            onChange={e => setDetailField('fd131_26', parseFloat(e.target.value) || 0)} data-contract-field="fd131_26"
          />

          <label>S/C NO</label>
          <input type="text" style={{ maxWidth: 120 }}
            disabled={disabled}
            value={getDetailField('fd131_18') as string}
            onChange={e => setDetailField('fd131_18', e.target.value)} data-contract-field="fd131_18"
          />

          <label>S/C 項次</label>
          <input type="number" style={{ maxWidth: 80 }}
            disabled={disabled}
            value={getDetailField('fd131_17') as number || ''}
            onChange={e => setDetailField('fd131_17', parseInt(e.target.value) || 0)} data-contract-field="fd131_17"
          />

          <label>允收量</label>
          <input type="number" readOnly style={{ maxWidth: 100 }}
            value={getDetailField('fd131_24') as number || ''} data-contract-field="fd131_24"
          />

          <label>允收增加量+</label>
          <input type="number" style={{ maxWidth: 80 }} step="0.01"
            disabled={disabled}
            value={getDetailField('fd131_242') as number || ''}
            onChange={e => setDetailField('fd131_242', parseFloat(e.target.value) || 0)} data-contract-field="fd131_242"
          />

          <label>允收比率+</label>
          <input type="number" style={{ maxWidth: 80 }} step="0.01"
            disabled={disabled}
            value={getDetailField('fd131_241') as number || ''}
            onChange={e => setDetailField('fd131_241', parseFloat(e.target.value) || 0)} data-contract-field="fd131_241"
          />

          <label>允收增加量-</label>
          <input type="number" style={{ maxWidth: 80 }} step="0.01"
            disabled={disabled}
            value={getDetailField('fd131_244') as number || ''}
            onChange={e => setDetailField('fd131_244', parseFloat(e.target.value) || 0)} data-contract-field="fd131_244"
          />

          <label>允收比率-</label>
          <input type="number" style={{ maxWidth: 80 }} step="0.01"
            disabled={disabled}
            value={getDetailField('fd131_243') as number || ''}
            onChange={e => setDetailField('fd131_243', parseFloat(e.target.value) || 0)} data-contract-field="fd131_243"
          />

          <label>工繳小計</label>
          <input type="text" readOnly style={{ maxWidth: 120, textAlign: 'right' }}
            value={fmt(sel.fd131_25)} data-contract-field="fd131_25"
          />

          <label>色卡號碼</label>
          <input type="text" style={{ maxWidth: 120 }}
            disabled={disabled}
            value={getDetailField('fd131_20') as string}
            onChange={e => setDetailField('fd131_20', e.target.value)} data-contract-field="fd131_20"
          />

          <label>打色通知單號</label>
          <input type="text" style={{ maxWidth: 120 }}
            disabled={disabled}
            value={getDetailField('fd131_21') as string}
            onChange={e => setDetailField('fd131_21', e.target.value)} data-contract-field="fd131_21"
          />

          <label>印花配色</label>
          <input type="text" style={{ maxWidth: 120 }}
            disabled={disabled}
            value={getDetailField('fd131_19') as string}
            onChange={e => setDetailField('fd131_19', e.target.value)} data-contract-field="fd131_19"
          />

          <label>客戶實際需求量</label>
          <input type="number" style={{ maxWidth: 100 }} step="0.1"
            disabled={disabled}
            value={getDetailField('fd131_27') as number || ''}
            onChange={e => setDetailField('fd131_27', parseFloat(e.target.value) || 0)} data-contract-field="fd131_27"
          />

          <label>顏色類別</label>
          <select style={{ maxWidth: 120 }}
            disabled={disabled}
            value={getDetailField('fd131_22') as string || ''}
            onChange={e => setDetailField('fd131_22', e.target.value)} data-contract-field="fd131_22"
          >
            <option value="">--</option>
            {Object.entries(COLOR_CATEGORY).map(([val, label]) => (
              <option key={val} value={val}>{label}</option>
            ))}
          </select>

          <label>顏色號碼</label>
          <input type="text" style={{ maxWidth: 120 }}
            disabled={disabled}
            value={getDetailField('fd131_23') as string || ''}
            onChange={e => setDetailField('fd131_23', e.target.value)} data-contract-field="fd131_23"
          />

          <label>CBNO.</label>
          <div className="lookup-field">
            <input type="text" style={{ width: 120 }} maxLength={10}
              disabled={disabled}
              value={getDetailField('fd131_11a') as string || ''}
              onChange={e => setDetailField('fd131_11a', e.target.value)}
              onBlur={() => resolveSingleName('cbNo', 'cbNo', getDetailField('fd131_11a') as string)} data-contract-field="fd131_11a"
            />
            <span className="lookup-name">{names.cbNo || '--'}</span>
          </div>
        </div>
      )}
    </>
  );
});

Tab4Detail.displayName = 'Tab4Detail';
export default Tab4Detail;
